import React from 'react';
import {
    Dialog,
    DialogContent,
    CircularProgress,
    Typography,
    Box,
    Divider,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Button,
    DialogActions,
} from '@mui/material';
import { Appointment, AptDate } from '../../models/ziphy';
import { DateTime } from 'luxon';
import { AppointmentEncounters } from '../../utils/fetchUtils';

interface Props {
    open: boolean;
    appointments:AppointmentEncounters;
    title?: string|JSX.Element;
    body?: string|JSX.Element;
    zcmId?: number|undefined;
    onClose: () => void;
    onConfirm: (encounterId: number) => Promise<void> | void;
}

function ziphydate2dt(t: AptDate) {
    return DateTime.fromObject({
        year: t.year, month: t.month, day: t.day, hour: t.hour, minute: t.minute, second: t.second
    });
}

const appointmentLabel = (option: Appointment, encounter?: number) => (
    `${option.id} - ${option?.appointed_start && ziphydate2dt(option?.appointed_start).toLocaleString() || ''} (${option?.status}) ${encounter ? ' - ' + encounter : ''}`
);

const EncounterSelectDialog: React.FC<Props> = ({
    open,
    appointments,
    title,
    body,
    zcmId,
    onClose,
    onConfirm,
}) => {
    const [selectedId, setSelectedId] = React.useState<number>();
    const [isConfirmEnabled, setIsConfirmEnabled] = React.useState<boolean>(false);

    const appointmentOptions = React.useMemo(() => {
        const optionList = [];
        const encounterList = appointments.encounters || [];
        let idx = 0
        for(const apt of appointments?.appointments || []) {

            for(const enc of apt?.encounter_ids || []) {
                if(zcmId) {
                    if(encounterList.find(e => e.id == enc && e.patient_id == zcmId)) {
                        optionList.push({ id:idx, apt:apt, enc:enc });
                    }
                } else {
                    optionList.push({ id:idx, apt:apt, enc:enc });
                }
                idx += 1;
            }
        }
        return optionList;
    }, [appointments, zcmId]);

    const handleConfirm = async () => {
        const aptData = appointmentOptions.find(e => e.id == selectedId);
        if(aptData) {
            await onConfirm(aptData.enc);
        }
    };

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            onClose={(event: object, reason: "backdropClick" | "escapeKeyDown") => {
                if (reason && reason === "backdropClick") return;
            }}
        >
            <DialogContent>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height="250px"
                    width="400px"
                >
                    <Typography variant="h5" align="center" gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="body1" align="center" paragraph>
                        {body}
                    </Typography>
                    <Divider sx={{ width: '100%', my: 2 }} />
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="encounter-select-label">Encounter</InputLabel>
                        <Select
                            labelId="encounter-select-label"
                            id="encounter-select"
                            value={selectedId}
                            label="Encounter"
                            onChange={(e) => {
                                const value = e.target.value;
                                setSelectedId(value as number);
                                setIsConfirmEnabled(!!value);
                            }}
                        >
                            {appointmentOptions.map((e) => (
                                <MenuItem key={e.id} value={e.id}>{appointmentLabel(e.apt, e.enc)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {appointmentOptions.length === 0 && (
                        <CircularProgress sx={{ mt: 2 }} />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
                <Button onClick={handleConfirm} color="primary" disabled={!isConfirmEnabled}>
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EncounterSelectDialog;